import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { t as translate } from "@/i18n/dict";
import { labelOf } from "@/i18n/I18nProvider";

function BannerCarousel({ banners, lang }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % banners.length), 4500);
    return () => clearInterval(id);
  }, [banners.length]);

  if (banners.length === 0) return null;
  const b = banners[idx];

  const handleClick = () => {
    const url = (b.cta_url || "").trim();
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener");
    } else {
      // Relative — navigate inside the SPA
      window.location.href = url.startsWith("/") || url.startsWith("?") ? url : `/${url}`;
    }
  };

  const clickable = !!(b.cta_url && b.cta_url.trim());

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") handleClick(); } : undefined}
      data-testid="landing-banner"
      data-cta-url={b.cta_url || ""}
      className={`relative w-full aspect-[16/10] sm:aspect-[16/9] overflow-hidden rounded-2xl bg-brand-dark shadow-soft ${
        clickable ? "cursor-pointer transition-transform active:scale-[0.99]" : ""
      }`}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={b.id}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img src={b.image_url} alt={labelOf(b.title, lang, "")} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-brand-bone">
            {labelOf(b.title, lang) && (
              <h1 className="editorial-h text-3xl sm:text-4xl font-semibold leading-tight" style={{ letterSpacing: "-0.02em" }}>
                {labelOf(b.title, lang)}
              </h1>
            )}
            {labelOf(b.subtitle, lang) && (
              <p className="font-sans text-sm sm:text-base text-brand-bone/85 mt-2 leading-snug max-w-md">
                {labelOf(b.subtitle, lang)}
              </p>
            )}
            {clickable && labelOf(b.cta_label, lang) && (
              <span className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full bg-brand-bone text-brand-dark text-xs font-bold uppercase tracking-widest" data-testid="banner-cta">
                {labelOf(b.cta_label, lang)} →
              </span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* dots */}
      {banners.length > 1 && (
        <div className="absolute bottom-3 right-4 flex gap-1.5 z-10" data-testid="landing-banner-dots">
          {banners.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "bg-brand-bone w-6" : "bg-brand-bone/45 w-1.5"
              }`}
              aria-label={`Banner ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function LanguageStep({ languages, currentLang, onSelect }) {
  const [banners, setBanners] = useState(null);
  const tt = (k) => translate(currentLang, k);

  useEffect(() => {
    api.get("/public/landing_banners").then((r) => setBanners(r.data || [])).catch(() => setBanners([]));
  }, []);

  const hasBanners = banners && banners.length > 0;

  return (
    <div className="min-h-[100dvh] flex flex-col warm-gradient">
      {/* Header section: banners OR fallback hero */}
      <div className="px-5 pt-6 sm:pt-10 pb-6 max-w-2xl mx-auto w-full">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-line text-[10px] tracking-[0.3em] uppercase text-brand-primary font-bold rise mb-5" data-testid="lang-overline">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" /> {tt("brand.name")}
        </div>

        {hasBanners ? (
          <BannerCarousel banners={banners} lang={currentLang} />
        ) : banners === null ? (
          <div className="w-full aspect-[16/10] bg-brand-elevated rounded-2xl animate-pulse" />
        ) : (
          // Fallback hero when no banners configured
          <>
            <h1 className="editorial-h h-display text-brand-dark rise" style={{ animationDelay: "60ms" }}>
              {tt("hero.title1")}<br />
              <span className="text-brand-primary">{tt("hero.title2")}</span>
            </h1>
            <p className="font-sans text-base sm:text-lg text-brand-muted mt-5 max-w-md leading-relaxed rise" style={{ animationDelay: "120ms" }}>
              {tt("hero.subtitle")}
            </p>
          </>
        )}
      </div>

      {/* Languages */}
      <div className="flex-1 px-5 pb-14 max-w-2xl mx-auto w-full">
        <p className="overline mb-3 rise" style={{ animationDelay: "200ms" }} data-testid="lang-choose-label">{tt("hero.pickLang")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="lang-grid">
          {languages.map((l, i) => {
            const active = l.code === currentLang;
            return (
              <motion.button
                key={l.code}
                whileTap={{ scale: 0.96 }}
                onClick={() => onSelect(l.code, l.dir || "ltr")}
                data-testid={`lang-option-${l.code}`}
                style={{ animationDelay: `${250 + i * 35}ms` }}
                className={`rise px-4 py-4 text-left rounded-2xl border transition-all bg-white ${
                  active
                    ? "border-brand-primary shadow-soft"
                    : "border-brand-line hover:border-brand-dark/40 hover:shadow-soft"
                }`}
              >
                <p className="font-display text-xl sm:text-2xl text-brand-dark leading-none tracking-tight">{l.name}</p>
                <p className="font-sans text-[10px] text-brand-muted mt-2 uppercase tracking-[0.2em] font-semibold">{l.code}</p>
              </motion.button>
            );
          })}
        </div>
        <p className="font-sans text-xs text-brand-muted mt-6 leading-relaxed">
          {tt("hero.availability")}
        </p>
      </div>
    </div>
  );
}
